<template>
  <AppPageContainer :custom="1600" :min-height="0">
    <section class="subscriptions" tabindex="-1">
      <AppPageHeader design="new" :title="canViewAll ? 'All availability subscriptions' : 'My availability subscriptions'" compact />
      <div class="ui-page">
        <AppFilterToolbar>
          <template #create>
            <app-button ref="createButton" design="new" type="button" @click.native="openCreate()">New subscription</app-button>
          </template>
          <app-select v-if="canViewAll" v-model="filters.owner" design="new" input-id="subscription-owner-filter" aria-label="User" :options="owners" label="name" :reduce="item => item.id" placeholder="User" :disabled="ownersLoading" @input="reloadFilters" />
          <app-select v-model="filters.property" design="new" input-id="subscription-property-filter" aria-label="Hotel" :options="propertyOptions" label="label" :reduce="item => item.value" placeholder="Hotel" @input="reloadFilters" />
          <app-select v-model="filters.lifecycle" design="new" input-id="subscription-status-filter" aria-label="Subscription status" :options="lifecycleOptions" label="label" :reduce="item => item.value" placeholder="Subscription status" @input="reloadFilters" />
          <app-select v-model="filters.availability" design="new" input-id="subscription-availability-filter" aria-label="Availability" :options="availabilityOptions" label="label" :reduce="item => item.value" placeholder="Availability" @input="reloadFilters" />
          <app-date-picker v-model="filters.stay_from" design="new" :input-attr="{ id: 'subscription-stay-from-filter', 'aria-label': 'Stay from' }" placeholder="Stay from" @input="reloadFilters" />
          <app-date-picker v-model="filters.stay_to" design="new" :input-attr="{ id: 'subscription-stay-to-filter', 'aria-label': 'Stay to' }" placeholder="Stay to" @input="reloadFilters" />
          <template #selection>
            <app-filters design="new" refined weight="medium" name="subscription-view" aria-label="Subscription view" :value="active ? 'active' : 'history'" :filters="viewOptions" @change="changeFilter($event === 'active')" />
          </template>
          <template #actions>
            <AppIconButton label="Open calendar" tooltip-placement="left" @click="$router.push({ name: 'hotel-management-calendar' })"><i class="pi pi-calendar" aria-hidden="true" /></AppIconButton>
          </template>
          <template v-if="hasFilters" #clear>
            <AppClearFiltersButton design="new" icon-only @clear="clearFilters" />
          </template>
        </AppFilterToolbar>
        <AppErrorState v-if="ownersError" :message="ownersError" retry-label="Retry users" @retry="loadOwners" />
        <AppContentSkeleton v-if="loading" label="Loading subscriptions" :columns="columns" min-width="1200px" />
        <AppErrorState v-else-if="pageError" :message="pageError" @retry="loadList(page)" />
        <template v-else>
          <AppCard v-if="rows.length" design="new" variant="table">
            <AppDataTable design="new" density="dense" :columns="columns" :rows="rows" min-width="1200px" clickable @row-click="openDetail($event.row)">
              <template #cell-property="{ row }">
                <AppTableText :text="row.property_name"><router-link class="ui-link" :to="{ name: 'hotel-management-availability-subscriptions', query: { subscription: row.id } }">{{ row.property_name }}</router-link></AppTableText>
              </template>
              <template #cell-room="{ row }"><AppTableText :text="row.room_type_name" /></template>
              <template #cell-owner="{ row }"><AppTableUser :name="row.owner_name" /></template>
              <template #cell-stay="{ row }"><AppTableText :text="`${row.check_in} → ${row.check_out}`" /></template>
              <template #cell-availability="{ row }"><AppStatusBadge design="new" compact :tone="stateTone(row.availability)" :label="stateLabel(row.availability)" /></template>
              <template #cell-lifecycle="{ row }"><AppStatusBadge design="new" compact :tone="lifecycleTone(row.lifecycle)" :label="lifecycleLabel(row.lifecycle)" /></template>
              <template #cell-expires_at="{ row }"><AppTableText :text="dateTime(row.expires_at)" /></template>
            </AppDataTable>
          </AppCard>
          <app-no-results v-else design="new" :title="hasFilters ? 'No matching subscriptions' : active ? 'No active subscriptions' : 'No completed subscriptions yet'" text="" />
          <AppPagination v-if="rows.length || count" design="new" placement="embedded" :page="page" :pages="pages" :count="count" :visible-count="rows.length" :page-size="pageSize" :loading="loading" label="Subscription pages" @change="loadList" />
        </template>
      </div>

    <modal name="availability-subscription-create" width="90%" :max-width="720" :focus-trap="true" height="auto" :adaptive="true" :scrollable="true" :click-to-close="!busy" class="subscription-overlay" @opened="focusModal('createHeader')" @before-close="guardClose" @closed="afterCreateClosed">
      <div class="subscriptions__dialog">
        <AppModalHeader ref="createHeader" design="new" title="New availability subscription" eyebrow="Room availability" title-id="subscription-create-title" @close="closeCreate" />
        <div class="subscriptions__modal-body">
          <AppCard design="new" title="Stay details" title-tag="h3">
            <p v-if="actionError" role="alert" class="subscriptions__error">{{ actionError }}</p>
            <SubscriptionForm :key="requestId" :initial="createInitial" :busy="busy" @save="create" />
          </AppCard>
        </div>
      </div>
    </modal>

    <modal name="availability-subscription-detail" width="90%" :max-width="960" :focus-trap="true" height="auto" :adaptive="true" :scrollable="true" :click-to-close="!busy" class="subscription-overlay" @opened="focusModal('detailHeader')" @before-close="guardClose" @closed="afterDetailClosed">
      <div class="subscriptions__dialog">
        <AppModalHeader ref="detailHeader" design="new" :title="detail ? `${detail.property_name} · ${detail.room_type_name}` : 'Availability subscription'" eyebrow="Room availability" title-id="subscription-detail-title" @close="closeDetail" />
        <div class="subscriptions__modal-body" :aria-busy="detailLoading">
          <div v-if="detailError" role="alert" class="subscriptions__error subscriptions__actions"><span>{{ detailError }}</span><app-button design="new" theme="dark" @click.native="refreshDetail">Retry</app-button></div>
          <div v-if="!detail && !detailError" class="subscriptions__skeleton" role="status" aria-label="Loading subscription"><span v-for="n in 6" :key="n" aria-hidden="true" /></div>
          <template v-if="detail">
            <div class="subscriptions__summary-grid">
              <AppCard design="new" title="Stay details" title-tag="h3">
                <dl class="subscriptions__facts">
                  <div v-if="canViewAll"><dt>Owner</dt><dd>{{ detail.owner_name }}</dd></div>
                  <div><dt>Hotel</dt><dd>{{ detail.property_name }}</dd></div>
                  <div><dt>Room type</dt><dd>{{ detail.room_type_name }}</dd></div>
                  <div><dt>Check-in / check-out</dt><dd>{{ detail.check_in }} → {{ detail.check_out }}</dd></div>
                  <div><dt>Rooms required</dt><dd>{{ detail.required_rooms }}</dd></div>
                </dl>
              </AppCard>
              <AppCard design="new" title="Monitoring" title-tag="h3">
                <template #actions><AppStatusBadge design="new" :tone="lifecycleTone(detail.lifecycle)" :label="lifecycleLabel(detail.lifecycle)" /></template>
                <dl class="subscriptions__facts">
                  <div><dt>Availability</dt><dd><AppStatusBadge design="new" :tone="stateTone(detail.availability)" :label="stateLabel(detail.availability)" /></dd></div>
                  <div><dt>Monitoring ends</dt><dd>{{ dateTime(detail.expires_at) }}</dd></div>
                  <div><dt>Last successful check</dt><dd>{{ dateTime(detail.last_checked_at) }}</dd></div>
                </dl>
                <p v-if="detail.last_error" role="alert" class="subscriptions__error">{{ detail.last_error }}</p>
              </AppCard>
            </div>

            <AppCard v-if="detail.lifecycle === 'initializing' || detail.setup_operation" design="new" title="Telegram connection" title-tag="h3">
              <template #actions><AppStatusBadge design="new" :tone="deliveryTone(detail.setup_operation && detail.setup_operation.execution_status)" :label="deliveryLabel(detail.setup_operation && detail.setup_operation.execution_status)" /></template>
              <p v-if="detail.lifecycle === 'initializing' && !detail.actions.confirm_setup_received">Connecting Telegram and checking availability. Please wait…</p>
              <p v-if="detail.setup_operation && detail.setup_operation.execution_status === 'succeeded'">{{ detail.is_owner ? 'We sent a message to your personal Telegram chat. Please check that it appeared.' : 'Telegram accepted the message sent to the subscription owner.' }}</p>
              <div v-if="detail.actions.confirm_setup_received" class="subscriptions__notice">
                <p>Telegram did not confirm whether the first message was sent. Check your personal chat. We will not send it again automatically.</p>
                <app-button ref="confirmAction" design="new" :disabled="busy" :aria-busy="busy" aria-label="I received the message" @click.native="runAction('confirm-setup-received', 'confirmAction')">I received the message</app-button>
              </div>
              <div v-if="detail.actions.retry_setup" role="alert" class="subscriptions__notice subscriptions__notice--error">
                <p>We could not send your Telegram message. Unblock/start the bot or contact an administrator, then try again.</p>
                <p>{{ detail.setup_operation.last_result.message }}</p>
                <app-button ref="retryAction" design="new" :disabled="busy" :aria-busy="busy" aria-label="Retry connection" @click.native="runAction('retry-setup', 'retryAction')">Retry connection</app-button>
              </div>
            </AppCard>

            <div class="subscriptions__actions">
              <app-button v-if="detail.actions.edit" design="new" theme="dark" :disabled="busy" @click.native="editing = !editing">{{ editing ? 'Close editor' : 'Edit conditions' }}</app-button>
              <app-button v-if="detail.actions.cancel" ref="cancelAction" design="new" theme="error" :disabled="busy" :aria-busy="busy" aria-label="Cancel subscription" @click.native="runAction('cancel', 'cancelAction')">Cancel subscription</app-button>
              <app-button v-if="['cancelled', 'expired'].includes(detail.lifecycle)" design="new" @click.native="copyAsNew">Create a new subscription</app-button>
            </div>
            <p v-if="!detail.is_owner" class="subscriptions__metadata">Only the owner can change or cancel this subscription.</p>
            <p v-if="actionError" role="alert" class="subscriptions__error">{{ actionError }}</p>
            <AppCard v-if="editing" design="new" title="Edit conditions" title-tag="h3"><SubscriptionForm :key="`${detail.id}-${detail.revision}`" :initial="detail" editing :busy="busy" @save="update" /></AppCard>

            <AppCard design="new" title="Timeline" title-tag="h3" :subtitle="eventsLoading && !events.length ? '… events' : `${eventCount} ${eventCount === 1 ? 'event' : 'events'}`" :aria-busy="eventsLoading">
              <div v-if="eventsError" class="subscriptions__error subscriptions__actions" role="alert"><span>{{ eventsError }}</span><app-button design="new" theme="dark" @click.native="loadEvents(eventPage)">Retry</app-button></div>
              <div v-else-if="eventsLoading" class="subscriptions__skeleton" role="status" aria-label="Loading timeline"><span v-for="n in 6" :key="n" aria-hidden="true" /></div>
              <app-no-results v-else-if="!events.length" design="new" title="No events yet" text="Availability checks and subscription updates will appear here." />
              <ol v-else class="subscriptions__timeline">
                <li v-for="event in events" :key="event.id" :class="`subscriptions__event--${eventTone(event)}`">
                  <time class="subscriptions__metadata" :datetime="event.created_at">{{ timelineDate(event.created_at) }}</time>
                  <strong>{{ eventLabel(event) }}</strong>
                  <div v-if="hasDeliveryIssue(event)" class="subscriptions__metadata">
                    <span :class="{ 'subscriptions__error': event.operation.execution_status !== 'unknown' }">Telegram notification: {{ event.operation.execution_status === 'unknown' ? 'Delivery unconfirmed' : 'Failed' }}</span>
                    <p v-if="event.operation.last_result && event.operation.last_result.message">{{ event.operation.last_result.message }}</p>
                  </div>
                  <span v-if="event.kind === 'conditions_changed'" class="subscriptions__metadata">{{ event.snapshot.check_in }} → {{ event.snapshot.check_out }} · {{ event.snapshot.room_type_name }} · {{ event.snapshot.required_rooms }} room(s)</span>
                </li>
              </ol>
              <AppPagination design="new" placement="inline" :page="eventPage" :pages="eventPages" :count="eventCount" :visible-count="events.length" :page-size="eventPageSize" :loading="eventsLoading" label="Timeline pages" @change="loadEvents" />
            </AppCard>
          </template>
        </div>
      </div>
    </modal>
    </section>
  </AppPageContainer>
</template>

<script>
import {
  getAvailabilitySubscriptions, getAvailabilitySubscription, getAvailabilitySubscriptionEvents,
  getAvailabilitySubscriptionOwners,
  postAvailabilitySubscription, patchAvailabilitySubscription, postAvailabilitySubscriptionAction,
} from '@/http'
import { availabilityLabel, newSubscriptionRequestId } from '@/utils/availabilitySubscriptions'
import SubscriptionForm from './Form.vue'
import AppPageHeader from '@/components/AppPageHeader'
import AppCard from '@/components/AppCard'
import AppDataTable from '@/components/AppDataTable'
import AppPagination from '@/components/AppPagination'
import AppStatusBadge from '@/components/AppStatusBadge'
import AppModalHeader from '@/components/AppModalHeader'
import AppClearFiltersButton from '@/components/AppClearFiltersButton'
import AppContentSkeleton from '@/components/AppContentSkeleton'
import AppErrorState from '@/components/AppErrorState'
import AppTableText from '@/components/AppTableText'
import AppTableUser from '@/components/AppTableUser'

export default {
  name: 'AvailabilitySubscriptions',
  components: { SubscriptionForm, AppPageHeader, AppCard, AppDataTable, AppPagination, AppStatusBadge, AppModalHeader, AppClearFiltersButton, AppContentSkeleton, AppErrorState, AppTableText, AppTableUser },
  data() {
    return {
      rows: [],
      filters: { owner: null, property: null, lifecycle: null, availability: null, stay_from: null, stay_to: null },
      owners: [],
      ownersLoading: false,
      ownersError: '',
      countRequest: 0,
      propertyOptions: [{ value: 'aishi', label: 'Aishi' }, { value: 'brubru', label: 'Brubru' }],
      availabilityOptions: [{ value: 'continuous', label: 'Available' }, { value: 'fragmented', label: 'Room change required' }, { value: 'unavailable', label: 'Not available' }],
      pageSize: 20,
      eventPageSize: 6,
      eventCount: 0,
      detailLoading: false,
      eventsLoading: false,
      eventsError: '',
      counts: { active: null, history: null },
      returnFocus: null,
      modalMode: null,
      otherCountLoading: false,
      active: true,
      count: 0,
      page: 1,
      pages: 1,
      loading: false,
      pageError: '',
      detail: null,
      detailError: '',
      events: [],
      eventPage: 1,
      eventPages: 1,
      busy: false,
      editing: false,
      actionError: '',
      createInitial: {},
      requestId: '',
      timer: null,
      listRequest: 0,
      destroyed: false,
    }
  },
  computed: {
    columns() {
      return [
        { key: 'property', label: 'Hotel', width: '180px' },
        { key: 'room', label: 'Room type', width: '180px' },
        ...(this.canViewAll ? [{ key: 'owner', label: 'Owner', width: '180px' }] : []),
        { key: 'stay', label: 'Stay', width: '210px' },
        { key: 'required_rooms', label: 'Rooms', align: 'right', width: '70px' },
        { key: 'availability', label: 'Availability', width: '250px' },
        { key: 'lifecycle', label: 'Subscription', width: '130px' },
        { key: 'expires_at', label: 'Monitoring ends', width: '180px' },
      ]
    },
    canViewAll() { return this.$store.state.userData.is_superuser || this.$store.state.userGroups.some(group => group.code === 'general_manager') },
    hasFilters() { return Object.values(this.filters).some(value => value !== null && value !== '') },
    lifecycleOptions() {
      return (this.active ? ['initializing', 'setup_failed', 'active'] : ['cancelled', 'expired']).map(value => ({ value, label: this.lifecycleLabel(value) }))
    },
    viewOptions() {
      return ['active', 'history'].map(key => ({ value: key, label: `${key === 'active' ? 'Active' : 'History'} (${((key === 'active') === this.active ? this.loading : this.otherCountLoading) ? '…' : this.counts[key] === null ? '—' : this.counts[key]})` }))
    },
  },
  watch: {
    '$route.query.subscription'() { this.syncDetail() },
  },
  mounted() {
    this.loadList()
    this.loadOtherCount()
    if (this.canViewAll) this.loadOwners()
    this.syncDetail()
    if (this.$route.query.create === '1') {
      const { property, room_type, check_in, check_out } = this.$route.query
      this.openCreate({ property: property || 'aishi', room_type: room_type ? Number(room_type) : null, check_in, check_out })
    }
  },
  beforeDestroy() {
    this.destroyed = true
    clearTimeout(this.timer)
  },
  methods: {
    listFilters() {
      return Object.fromEntries(Object.entries(this.filters).filter(([key, value]) => value !== null && value !== '' && (key !== 'owner' || this.canViewAll)))
    },
    reloadFilters() { this.loadList(); this.loadOtherCount() },
    clearFilters() {
      this.filters = { owner: null, property: null, lifecycle: null, availability: null, stay_from: null, stay_to: null }
      this.reloadFilters()
    },
    async loadOwners() {
      this.ownersLoading = true
      this.ownersError = ''
      try {
        const { data } = await getAvailabilitySubscriptionOwners()
        if (!this.destroyed) this.owners = data
      } catch (error) { this.ownersError = 'Users could not be loaded.' } finally { this.ownersLoading = false }
    },
    stateLabel(value) { return availabilityLabel(value).replace(/^[^A-Za-z]+/, '') },
    stateTone(value) { return { continuous: 'green', fragmented: 'amber', unavailable: 'red' }[value] || 'neutral' },
    lifecycleLabel(value) { return { initializing: 'Connecting', setup_failed: 'Setup failed', active: 'Active', cancelled: 'Cancelled', expired: 'Expired' }[value] || value },
    lifecycleTone(value) { return { initializing: 'blue', setup_failed: 'amber', active: 'green', cancelled: 'red' }[value] || 'neutral' },
    deliveryLabel(value) { return { pending: 'Queued', running: 'Sending', succeeded: 'Accepted by Telegram', failed: 'Failed', unknown: 'Unknown', cancelled: 'Cancelled', superseded: 'Superseded' }[value] || 'Pending' },
    deliveryTone(value) { return { pending: 'blue', running: 'blue', succeeded: 'green', failed: 'red', cancelled: 'red' }[value] || 'neutral' },
    eventTone(event) { return event.new_state ? this.stateTone(event.new_state) : event.kind === 'cancelled' ? 'red' : event.kind === 'setup_confirmed' ? 'green' : 'blue' },
    focusModal(ref) {
      const header = this.$refs[ref]
      if (!header) return
      const modal = header.$el.closest('[role="dialog"]')
      if (modal) modal.setAttribute('aria-labelledby', header.titleId)
      header.focusClose()
    },
    restoreFocus() {
      this.$nextTick(() => {
        if (this.modalMode) return
        if (this.returnFocus?.isConnected && this.returnFocus !== document.body) this.returnFocus.focus()
        else if (this.$refs.createButton) this.$refs.createButton.$el.focus()
        this.returnFocus = null
      })
    },
    afterCreateClosed() { if (this.modalMode === 'create') { this.modalMode = null; this.restoreFocus() } },
    afterDetailClosed() {
      if (this.modalMode !== 'detail') return
      this.modalMode = null
      clearTimeout(this.timer)
      if (this.$route.query.subscription) this.$router.replace({ name: 'hotel-management-availability-subscriptions' })
      this.restoreFocus()
    },
    guardClose(event) { if (this.busy) event.cancel() },
    closeCreate() { if (!this.busy) this.$modal.hide('availability-subscription-create') },
    openDetail(row) { this.returnFocus = document.activeElement; this.$router.push({ name: 'hotel-management-availability-subscriptions', query: { subscription: row.id } }) },
    async loadOtherCount() {
      const request = ++this.countRequest
      const active = !this.active
      this.otherCountLoading = true
      const filters = this.listFilters()
      delete filters.lifecycle
      try {
        const { data } = await getAvailabilitySubscriptions({ ...filters, active, page_size: 1 })
        if (request === this.countRequest && !this.destroyed) this.counts[active ? 'active' : 'history'] = data.count
      } catch (error) {
        if (request === this.countRequest && !this.destroyed) this.counts[active ? 'active' : 'history'] = null
      } finally {
        if (request === this.countRequest && !this.destroyed) this.otherCountLoading = false
      }
    },
    dateTime(value) { return value ? new Date(value).toLocaleString() : 'Not checked yet' },
    timelineDate(value) {
      if (!value) return '—'
      const date = new Date(value)
      return `${String(date.getDate()).padStart(2, '0')} ${date.toLocaleString('en-US', { month: 'short' })} ${date.getFullYear()}`
    },
    timelineStateLabel(value) { return { continuous: 'Available', fragmented: 'Room change required', unavailable: 'Not available' }[value] || this.stateLabel(value) },
    hasDeliveryIssue(event) { return ['failed', 'blocked', 'unknown'].includes(event.operation?.execution_status) },
    eventLabel(event) {
      if (event.kind === 'transition' || event.kind === 'initial') return this.timelineStateLabel(event.new_state)
      return { setup: 'Telegram connection requested', setup_confirmed: this.detail?.is_owner ? 'You confirmed receipt of the Telegram message' : 'The owner confirmed receipt of the Telegram message', conditions_changed: 'Conditions changed', cancelled: 'Subscription cancelled', expired: 'Monitoring period ended' }[event.kind] || event.kind
    },
    errorText(error) {
      if (error.response?.status === 404) return 'Subscription not found or no longer accessible.'
      return error.response?.data?.details || 'The request could not be completed. Please try again.'
    },
    async loadList(page = 1) {
      const request = ++this.listRequest
      this.page = page
      this.loading = true
      this.pageError = ''
      const active = this.active
      try {
        const { data } = await getAvailabilitySubscriptions({ ...this.listFilters(), active, page, page_size: this.pageSize })
        if (request !== this.listRequest || this.destroyed) return
        this.rows = data.results
        this.pageError = ''
        this.count = data.count
        this.counts[active ? 'active' : 'history'] = data.count
        this.pages = data.pages || 1
      } catch (error) {
        if (request === this.listRequest && !this.destroyed) this.pageError = this.errorText(error)
      } finally {
        if (request === this.listRequest && !this.destroyed) {
          this.loading = false
        }
      }
    },
    changeFilter(active) { this.active = active; this.filters.lifecycle = null; this.reloadFilters() },
    openCreate(initial = {}) {
      if (!this.modalMode) this.returnFocus = document.activeElement
      this.modalMode = 'create'
      this.actionError = ''
      this.createInitial = initial
      this.requestId = newSubscriptionRequestId()
      this.$modal.show('availability-subscription-create')
    },
    async create(payload) {
      this.busy = true
      this.actionError = ''
      try {
        const { data } = await postAvailabilitySubscription({ ...payload, request_id: this.requestId })
        this.busy = false
        this.modalMode = 'detail'
        this.$modal.hide('availability-subscription-create')
        await this.$router.replace({ name: 'hotel-management-availability-subscriptions', query: { subscription: data.id } })
        this.loadList()
      } catch (error) { this.actionError = this.errorText(error) } finally { this.busy = false }
    },
    syncDetail() {
      clearTimeout(this.timer)
      this.editing = false
      this.actionError = ''
      this.detailError = ''
      this.detail = null
      this.events = []
      this.eventCount = 0
      this.eventsError = ''
      this.eventPage = 1
      if (!this.$route.query.subscription) { this.$modal.hide('availability-subscription-detail'); return }
      if (!this.returnFocus) this.returnFocus = document.activeElement
      this.modalMode = 'detail'
      this.$modal.show('availability-subscription-detail')
      this.refreshDetail()
    },
    async refreshDetail() {
      clearTimeout(this.timer)
      const id = this.$route.query.subscription
      if (!id || this.destroyed) return
      this.detailLoading = true
      try {
        const { data } = await getAvailabilitySubscription(id)
        if (id !== this.$route.query.subscription || this.destroyed) return
        if (!this.editing) this.detail = data
        this.detailError = ''
        await this.loadEvents(this.eventPage, true)
        if (data.actions.cancel) this.timer = setTimeout(this.refreshDetail, data.lifecycle === 'initializing' ? 2000 : 10000)
      } catch (error) { if (id === this.$route.query.subscription) this.detailError = this.errorText(error) } finally { this.detailLoading = false }
    },
    async loadEvents(page = 1, background = false) {
      const id = this.$route.query.subscription
      if (!id) return
      this.eventsLoading = !background || !this.events.length
      this.eventsError = ''
      try {
        const { data } = await getAvailabilitySubscriptionEvents(id, { page, page_size: this.eventPageSize })
        if (id !== this.$route.query.subscription || this.destroyed) return
        this.events = data.results
        this.eventCount = data.count
        this.eventPages = data.pages || 1
        this.eventPage = page
      } catch (error) { this.eventsError = this.errorText(error) } finally { this.eventsLoading = false }
    },
    closeDetail() {
      if (this.busy) return
      clearTimeout(this.timer)
      this.$modal.hide('availability-subscription-detail')

    },
    async runAction(action, buttonRef) {
      if (this.$refs[buttonRef]) this.$refs[buttonRef].preload = true
      this.busy = true
      this.actionError = ''
      try {
        const { data } = await postAvailabilitySubscriptionAction(this.detail.id, action, { revision: this.detail.revision })
        this.detail = data
        this.editing = false
        await this.refreshDetail()
        this.loadList(1)
        this.loadOtherCount()
        this.$notify({ type: 'success', title: action === 'cancel' ? 'Subscription cancelled' : action === 'retry-setup' ? 'Connection retry requested' : 'Receipt confirmed' })
      } catch (error) { this.actionError = this.errorText(error) } finally { this.busy = false; if (this.$refs[buttonRef]) this.$refs[buttonRef].preload = false }
    },
    async update(payload) {
      this.busy = true
      this.actionError = ''
      try {
        const { data } = await patchAvailabilitySubscription(this.detail.id, { ...payload, revision: this.detail.revision })
        this.editing = false
        this.detail = data
        await this.refreshDetail()
        this.loadList(1)
        this.$notify({ type: 'success', title: 'Subscription conditions updated' })
      } catch (error) { this.actionError = this.errorText(error) } finally { this.busy = false }
    },
    copyAsNew() {
      const initial = { ...this.detail, check_in: null, check_out: null }
      this.closeDetail()
      this.$router.replace({ name: 'hotel-management-availability-subscriptions' })
      this.openCreate(initial)
    },
  },
}
</script>

<style lang="scss" scoped>
.subscriptions__actions { display: flex; align-items: center; flex-wrap: wrap; gap: $ui-space-12; }
.subscriptions__metadata { display: block; color: $ui-muted; font-size: $ui-font-size-metadata; line-height: 1.5; overflow-wrap: anywhere; }
.subscriptions__dialog { color: $ui-text; font: $ui-font-size-body $ui-font-body; line-height: 1.45; overflow-wrap: anywhere; }
.subscriptions__modal-body { display: grid; gap: $ui-space-16; padding: $ui-space-24; background: $ui-surface-soft; }
.subscriptions__summary-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: $ui-space-16; }
.subscriptions__facts { margin: 0; display: grid; gap: $ui-space-16; }
.subscriptions__facts dt { color: $ui-muted; font-size: $ui-font-size-metadata; margin-bottom: $ui-space-4; }
.subscriptions__facts dd { margin: 0; color: $ui-ink; font-weight: 600; }
.subscriptions__error { color: $ui-red; }
.subscriptions__notice { padding: $ui-space-16; background: $ui-blue-soft; border-radius: $ui-panel-radius; }
.subscriptions__notice--error { background: $ui-red-soft; }
.subscriptions__dialog p { margin: 0 0 $ui-space-12; }
.subscriptions__dialog p:last-child { margin-bottom: 0; }
.subscriptions__timeline { list-style: none; margin: 0 0 0 $ui-space-8; padding: 0 0 0 $ui-space-24; border-left: 1px solid $ui-line-strong; }
.subscriptions__timeline li { position: relative; padding-bottom: $ui-space-24; }
.subscriptions__timeline li:last-child { padding-bottom: 0; }
.subscriptions__timeline li::before { content: ''; position: absolute; left: -29px; top: $ui-space-4; width: 9px; height: 9px; border-radius: 50%; background: $ui-blue; }
.subscriptions__timeline .subscriptions__event--green::before { background: $ui-green; }
.subscriptions__timeline .subscriptions__event--amber::before { background: $ui-amber; }
.subscriptions__timeline .subscriptions__event--red::before { background: $ui-red; }
.subscriptions__timeline .subscriptions__event--neutral::before { background: $ui-muted; }
.subscriptions__timeline strong { display: block; color: $ui-ink; font-size: $ui-font-size-body; font-weight: 600; margin: $ui-space-4 0 $ui-space-8; }
.subscriptions__skeleton { display: grid; gap: $ui-space-16; }
.subscriptions__skeleton span { display: block; height: $ui-space-32; background: $ui-line; border-radius: $ui-control-radius; }
.subscription-overlay :deep(.vm--modal) { border-radius: $ui-overlay-radius; }
@media (max-width: 760px) { .subscriptions__summary-grid { grid-template-columns: minmax(0, 1fr); } }
@media (max-width: 600px) { .subscriptions__modal-body { padding: $ui-space-16; } }
</style>
